import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Save, Download } from "lucide-react";

import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintWithGlyph, PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { downloadPdfBase64 } from "@/lib/miscPrintingApi";
import { EM_DASH, cn } from "@/lib/utils";

import {
  CORRECTIVE_ACTION_SOURCES,
  CORRECTIVE_ACTION_STATUSES,
  CORRECTIVE_ACTIONS_LIST_KEY,
  correctiveActionSourceLabel,
  correctiveActionStatusLabel,
  createCorrectiveAction,
  type CorrectiveActionSource,
  type CorrectiveActionStatus,
} from "./correctiveActionsApi";
import { QualityCaseActivityCard } from "./QualityCaseActivityCard";
import { QualityCaseEventsCard } from "./QualityCaseEventsCard";
import { QualityCaseNcrsCard } from "./QualityCaseNcrsCard";
import { QualityCaseNotesCard } from "./QualityCaseNotesCard";
import { QualityCaseRcasCard } from "./QualityCaseRcasCard";
import { QualityCaseRecordGapsCard } from "./QualityCaseRecordGapsCard";
import { QualityOwnerSelect } from "./QualityOwnerSelect";
import { QualityProductFamilyPicker } from "./QualityProductFamilyPicker";
import { QualityPurchaseOrderPicker } from "./QualityPurchaseOrderPicker";
import { QualityRmaPicker } from "./QualityRmaPicker";
import { QualitySalesOrderPicker } from "./QualitySalesOrderPicker";
import { QualityContainmentCard } from "./QualityContainmentCard";
import { QualityAnalysisTransferPanel } from "./QualityAnalysisTransferPanel";
import { createQualityRma } from "./rmaApi";
import {
  QUALITY_CASES_LIST_KEY,
  QUALITY_CASE_STATUSES,
  fetchQualityCaseDetail,
  fetchQualityCasePdf,
  linkQualityCaseCorrectiveAction,
  linkQualityCaseRma,
  qualityCaseLabel,
  qualityCaseQueryKey,
  qualityCaseStatusLabel,
  unlinkQualityCaseCorrectiveAction,
  unlinkQualityCaseRma,
  updateQualityCase,
  type QualityCaseDetailResponse,
  type QualityCaseStatus,
  type UpdateQualityCaseInput,
} from "./casesApi";
import { qualityCaseDetailQueryState } from "./qualityCaseDetailQueryState";

const DETAIL_HELP = [
  "The case is the folder. Broadcast containment here so people can acknowledge shop-floor controls.",
  "Work comments and a final report in the investigation. Print or download a traveler to take to the bench.",
  "Link QC incidents for the devices in scope. Create or link a CAR when you are ready for the ISO closure snapshot.",
  "Closing an investigation moves the case to Actions open. Starting another investigation after a failed review",
  "creates a new investigation row; closed investigations stay snapshots.",
].join(" ");

const CASE_FIELDS_HELP =
  "Title, status, owner, and linked sales orders or purchase orders identify the dossier. RMAs and QC incidents are linked as chips, not as the Source field. Source is an ISO classification for a later CAR.";

const SOURCE_HELP =
  "Optional ISO origin for a corrective action report (customer complaint, supplier, internal audit, and so on). It is not a substitute for linking RMAs or QC incidents. When you create a case, the hub infers RMA vs QC incident vs other from those links.";

function statusTone(status: QualityCaseStatus): BadgeTone {
  switch (status) {
    case "closed":
      return "success";
    case "cancelled":
      return "neutral";
    case "investigating":
    case "pending_effectiveness":
      return "info";
    default:
      return "warning";
  }
}

function formatTimestamp(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

type CaseForm = {
  title: string;
  status: QualityCaseStatus;
  sourceType: CorrectiveActionSource;
  ownerUserId: string | null;
  salesOrderIds: number[];
  purchaseOrderIds: number[];
  productFamilies: string[];
};

function formFromDetail(detail: QualityCaseDetailResponse): CaseForm {
  return {
    title: detail.case.title,
    status: detail.case.status,
    sourceType: detail.case.sourceType,
    ownerUserId: detail.case.ownerUserId,
    salesOrderIds: detail.salesOrders.map((row) => row.id),
    purchaseOrderIds: detail.purchaseOrders.map((row) => row.id),
    productFamilies: detail.productFamilies.map((row) => row.key),
  };
}

function buildCasePatch(form: CaseForm, baseline: CaseForm): UpdateQualityCaseInput {
  const patch: UpdateQualityCaseInput = {};
  if (form.title !== baseline.title) patch.title = form.title.trim();
  if (form.status !== baseline.status) patch.status = form.status;
  if (form.sourceType !== baseline.sourceType) patch.sourceType = form.sourceType;
  if (form.ownerUserId !== baseline.ownerUserId) patch.ownerUserId = form.ownerUserId;
  if (form.productFamilies.join(",") !== baseline.productFamilies.join(",")) {
    patch.productFamilies = form.productFamilies;
  }
  if (form.salesOrderIds.join(",") !== baseline.salesOrderIds.join(",")) {
    patch.salesOrderIds = form.salesOrderIds;
  }
  if (form.purchaseOrderIds.join(",") !== baseline.purchaseOrderIds.join(",")) {
    patch.purchaseOrderIds = form.purchaseOrderIds;
  }
  return patch;
}

export function QualityCaseDetailPage() {
  const { id } = useParams();
  const reportId = Number(id);
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("quality");

  const detailQuery = useQuery({
    queryKey: qualityCaseQueryKey(reportId),
    queryFn: () => fetchQualityCaseDetail(reportId),
    enabled: Number.isFinite(reportId) && reportId > 0,
  });

  const qualityCase = detailQuery.data?.case;
  const [form, setForm] = useState<CaseForm | null>(null);
  const [carIdToLink, setCarIdToLink] = useState("");
  const [showCreateRma, setShowCreateRma] = useState(false);
  const [createRmaComplaint, setCreateRmaComplaint] = useState("");
  const [showCreateCar, setShowCreateCar] = useState(false);
  const [createCarTitle, setCreateCarTitle] = useState("");
  const [createCarSource, setCreateCarSource] = useState<CorrectiveActionSource>("rma");
  const [createCarProblem, setCreateCarProblem] = useState("");
  const [createCarContainment, setCreateCarContainment] = useState("");
  const [createCarTargetCloseDate, setCreateCarTargetCloseDate] = useState("");
  const [createCarQualityIncidentId, setCreateCarQualityIncidentId] = useState("");

  useEffect(() => {
    if (detailQuery.data == null) return;
    setForm(formFromDetail(detailQuery.data));
  }, [detailQuery.data]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: qualityCaseQueryKey(reportId) });
    await queryClient.invalidateQueries({ queryKey: QUALITY_CASES_LIST_KEY });
  };

  const saveCaseMutation = useMutation({
    mutationFn: (patch: UpdateQualityCaseInput) => updateQualityCase(reportId, patch),
    onSuccess: async () => {
      await invalidate();
      showToast({ tone: "success", message: "Case saved." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const savePdfMutation = useMutation({
    mutationFn: () => fetchQualityCasePdf(reportId),
    onSuccess: (payload) => {
      downloadPdfBase64(payload.pdfBase64, payload.filename);
      showToast({ tone: "success", message: "Saved case PDF." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const linkRmaMutation = useMutation({
    mutationFn: (rmaId: number) => linkQualityCaseRma(reportId, rmaId),
    onSuccess: async () => {
      await invalidate();
      showToast({ tone: "success", message: "RMA linked." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const createRmaMutation = useMutation({
    mutationFn: async () => {
      const created = await createQualityRma({ complaint: createRmaComplaint.trim() || null });
      await linkQualityCaseRma(reportId, created.rma.id);
      return created.rma;
    },
    onSuccess: async (rma) => {
      setShowCreateRma(false);
      setCreateRmaComplaint("");
      await invalidate();
      showToast({ tone: "success", message: `Created ${rma.rmaNumber} and linked it.` });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const unlinkRmaMutation = useMutation({
    mutationFn: (rmaId: number) => unlinkQualityCaseRma(reportId, rmaId),
    onSuccess: async () => {
      await invalidate();
      showToast({ tone: "success", message: "RMA unlinked." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const linkCarMutation = useMutation({
    mutationFn: (correctiveActionReportId: number) =>
      linkQualityCaseCorrectiveAction(reportId, correctiveActionReportId),
    onSuccess: async () => {
      setCarIdToLink("");
      await invalidate();
      showToast({ tone: "success", message: "Corrective action report linked." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const unlinkCarMutation = useMutation({
    mutationFn: (carReportId: number) => unlinkQualityCaseCorrectiveAction(reportId, carReportId),
    onSuccess: async () => {
      await invalidate();
      showToast({ tone: "success", message: "Corrective action report unlinked." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const createCarMutation = useMutation({
    mutationFn: () => {
      const qualityIncidentId =
        createCarQualityIncidentId.trim() === "" ? undefined : Number(createCarQualityIncidentId);
      return createCorrectiveAction({
        title: createCarTitle.trim(),
        sourceType: createCarSource,
        problemDescription: createCarProblem.trim() || undefined,
        immediateContainment: createCarContainment.trim() || undefined,
        targetCloseDate: createCarTargetCloseDate || undefined,
        ownerUserId: qualityCase?.ownerUserId ?? undefined,
        clientFsAccountId: qualityCase?.clientFsAccountId ?? undefined,
        salesOrderId: qualityCase?.salesOrderId ?? undefined,
        qualityIncidentId: Number.isFinite(qualityIncidentId) ? qualityIncidentId : undefined,
        qualityCaseId: reportId,
      });
    },
    onSuccess: async (created) => {
      setShowCreateCar(false);
      setCreateCarTitle("");
      setCreateCarProblem("");
      setCreateCarContainment("");
      setCreateCarTargetCloseDate("");
      setCreateCarQualityIncidentId("");
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: CORRECTIVE_ACTIONS_LIST_KEY });
      showToast({ tone: "success", message: `Opened ${created.carNumber} and linked it to this case.` });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  if (!Number.isFinite(reportId) || reportId < 1) {
    return <p className="text-sm text-red-700">Invalid quality case id.</p>;
  }

  const screen = qualityCaseDetailQueryState({
    isError: detailQuery.isError,
    isPending: detailQuery.isPending,
    hasCase: qualityCase != null,
  });
  if (screen === "error") {
    return <p className="text-sm text-red-700">{(detailQuery.error as Error).message}</p>;
  }
  if (screen === "loading" || form == null) {
    return <p className="text-sm text-slate-600">Loading quality case…</p>;
  }

  const detail = detailQuery.data;
  if (detail == null || qualityCase == null) {
    return <p className="text-sm text-red-700">Quality case was not found.</p>;
  }

  const baseline = formFromDetail(detail);
  const caseDirty = Object.keys(buildCasePatch(form, baseline)).length > 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <PageTitleWithHelp hoverHint={DETAIL_HELP} breadcrumbLabel={qualityCaseLabel(qualityCase.id)}>
            {qualityCaseLabel(qualityCase.id)}
          </PageTitleWithHelp>
          <div className="flex flex-wrap gap-2">
            <Badge tone={statusTone(qualityCase.status)}>{qualityCaseStatusLabel(qualityCase.status)}</Badge>
            <Badge tone="neutral">{correctiveActionSourceLabel(qualityCase.sourceType)}</Badge>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            disabled={caseDirty || saveCaseMutation.isPending || savePdfMutation.isPending}
            title={caseDirty || saveCaseMutation.isPending ? "Save the case before you download." : undefined}
            onClick={() => savePdfMutation.mutate()}
          >
            {savePdfMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Save to PDF
          </Button>
          {writesAllowed ? (
            <HubSaveButton
              dirty={caseDirty}
              isLoading={saveCaseMutation.isPending}
              onClick={() => {
                try {
                  const patch = buildCasePatch(form, baseline);
                  if (Object.keys(patch).length === 0) {
                    return;
                  }
                  saveCaseMutation.mutate(patch);
                } catch (error) {
                  showToast({ tone: "error", message: (error as Error).message });
                }
              }}
            >
              {saveCaseMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save case
            </HubSaveButton>
          ) : null}
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint={CASE_FIELDS_HELP}>Case</CardTitleWithHelp>
        </CardHeader>
        <CardBody className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label htmlFor="case-title">Title</Label>
            <Input
              id="case-title"
              value={form.title}
              disabled={!writesAllowed}
              onChange={(event) => setForm((current) => (current ? { ...current, title: event.target.value } : current))}
            />
          </div>
          <div>
            <p id="case-status" className="text-sm font-medium text-slate-700">
              Status
            </p>
            <p className="mt-1.5 text-sm text-slate-900">{qualityCaseStatusLabel(form.status)}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {QUALITY_CASE_STATUSES.filter((status) => status !== form.status).map((status) => (
                <Button
                  key={status}
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!writesAllowed}
                  onClick={() =>
                    setForm((current) => (current ? { ...current, status } : current))
                  }
                >
                  {qualityCaseStatusLabel(status)}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1">
              <Label htmlFor="case-source">Source (CAR classification)</Label>
              <HoverHintWithGlyph hoverHint={SOURCE_HELP} size="sm" />
            </div>
            <select
              id="case-source"
              className={cn(
                "mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
                "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
              )}
              value={form.sourceType}
              disabled={!writesAllowed}
              onChange={(event) =>
                setForm((current) =>
                  current ? { ...current, sourceType: event.target.value as CorrectiveActionSource } : current,
                )
              }
            >
              {CORRECTIVE_ACTION_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {correctiveActionSourceLabel(source)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="case-owner">Owner</Label>
            <QualityOwnerSelect
              id="case-owner"
              value={form.ownerUserId}
              currentLabel={qualityCase.ownerDisplay}
              disabled={!writesAllowed}
              onChange={(ownerUserId) =>
                setForm((current) => (current ? { ...current, ownerUserId } : current))
              }
            />
          </div>
          <div>
            <Label htmlFor="case-sales-order">Sales orders</Label>
            <QualitySalesOrderPicker
              id="case-sales-order"
              values={form.salesOrderIds}
              knownLabels={Object.fromEntries(detail.salesOrders.map((row) => [row.id, row.reference]))}
              disabled={!writesAllowed}
              onChange={(salesOrderIds) =>
                setForm((current) => (current ? { ...current, salesOrderIds } : current))
              }
            />
          </div>
          <div>
            <Label htmlFor="case-purchase-order">Purchase orders</Label>
            <QualityPurchaseOrderPicker
              id="case-purchase-order"
              values={form.purchaseOrderIds}
              knownLabels={Object.fromEntries(detail.purchaseOrders.map((row) => [row.id, row.reference]))}
              disabled={!writesAllowed}
              onChange={(purchaseOrderIds) =>
                setForm((current) => (current ? { ...current, purchaseOrderIds } : current))
              }
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="case-product-family">Product families</Label>
            <QualityProductFamilyPicker
              id="case-product-family"
              values={form.productFamilies}
              disabled={!writesAllowed}
              onChange={(productFamilies) =>
                setForm((current) => (current ? { ...current, productFamilies } : current))
              }
            />
          </div>
          <p className="md:col-span-2 text-xs text-slate-500">
            Opened {formatTimestamp(qualityCase.openedAt)} by {qualityCase.reportedByDisplay ?? qualityCase.reportedBy}
            {qualityCase.closedAt
              ? ` · Closed ${formatTimestamp(qualityCase.closedAt)} by ${qualityCase.closedByDisplay ?? qualityCase.closedBy ?? EM_DASH}`
              : ""}
          </p>
        </CardBody>
      </Card>

      <QualityContainmentCard
        caseId={qualityCase.id}
        notices={detail.containmentNotices}
        writesAllowed={writesAllowed}
        onBroadcastSuccess={async () => {
          await invalidate();
        }}
      />

      <QualityCaseNotesCard caseId={qualityCase.id} />

      <QualityAnalysisTransferPanel parent={{ qualityCaseId: qualityCase.id }} />

      <Card>
        <CardHeader className="flex flex-wrap items-center gap-2">
          <CardTitleWithHelp hoverHint="Customer or end-user return authorizations in scope for this case. Link an RMA that already exists, or create one and link it.">
            Linked RMAs
          </CardTitleWithHelp>
          {writesAllowed && !showCreateRma ? (
            <Button size="sm" variant="secondary" onClick={() => setShowCreateRma(true)}>
              New RMA
            </Button>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-4">
          {writesAllowed && showCreateRma ? (
            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              <div>
                <Label htmlFor="case-create-rma-complaint">Complaint</Label>
                <TextArea
                  id="case-create-rma-complaint"
                  value={createRmaComplaint}
                  rows={3}
                  onChange={(event) => setCreateRmaComplaint(event.target.value)}
                  placeholder="What the customer is returning and why"
                />
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="secondary" disabled={createRmaMutation.isPending} onClick={() => setShowCreateRma(false)}>
                  Cancel
                </Button>
                <Button disabled={createRmaMutation.isPending} onClick={() => createRmaMutation.mutate()}>
                  Create RMA
                </Button>
              </div>
            </div>
          ) : null}
          {writesAllowed ? (
            <QualityRmaPicker
              id="link-rma-id"
              values={detail.rmas.map((row) => row.id)}
              knownLabels={Object.fromEntries(detail.rmas.map((row) => [row.id, row.rmaNumber]))}
              onChange={(next) => {
                const linked = new Set(detail.rmas.map((row) => row.id));
                const added = next.find((rmaId) => !linked.has(rmaId));
                const removed = detail.rmas.find((row) => !next.includes(row.id));
                if (added != null) {
                  linkRmaMutation.mutate(added);
                } else if (removed != null) {
                  unlinkRmaMutation.mutate(removed.id);
                }
              }}
            />
          ) : null}
          {detail.rmas.length === 0 ? (
            <p className="text-sm text-slate-600">No RMAs linked to this case.</p>
          ) : (
            <ul className="flex flex-wrap gap-2 text-sm">
              {detail.rmas.map((row) => (
                <li key={row.id}>
                  <Link className="text-sky-700 hover:underline" to={`/quality/rmas/${row.id}`}>
                    {row.rmaNumber}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <QualityCaseEventsCard caseId={qualityCase.id} />

      <QualityCaseNcrsCard
        caseId={qualityCase.id}
        incidents={detail.incidents}
        writesAllowed={writesAllowed}
        onChanged={invalidate}
      />

      <QualityCaseRcasCard
        caseId={qualityCase.id}
        incidentIds={detail.incidents.map((incident) => incident.id)}
      />

      <Card>
        <CardHeader className="flex flex-wrap items-center gap-2">
          <CardTitleWithHelp hoverHint="Corrective action reports attached to this case. Create a new CAR from this case, or link an existing one. The CAR is the ISO closure artifact, not the live investigation workspace.">
            Linked corrective action reports
          </CardTitleWithHelp>
          {writesAllowed && !showCreateCar ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setShowCreateCar(true);
                if (createCarTitle.trim() === "") {
                  setCreateCarTitle(qualityCase.title);
                }
                setCreateCarSource(qualityCase.sourceType);
              }}
            >
              <Plus className="h-4 w-4" />
              New CAR
            </Button>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-4">
          {writesAllowed && showCreateCar ? (
            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="case-create-car-title">Title</Label>
                  <Input
                    id="case-create-car-title"
                    value={createCarTitle}
                    onChange={(event) => setCreateCarTitle(event.target.value)}
                    placeholder="Short summary of the nonconformity"
                  />
                </div>
                <div>
                  <Label htmlFor="case-create-car-target-close">Target close date</Label>
                  <Input
                    id="case-create-car-target-close"
                    type="date"
                    value={createCarTargetCloseDate}
                    onChange={(event) => setCreateCarTargetCloseDate(event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="case-create-car-source">Source</Label>
                  <select
                    id="case-create-car-source"
                    className={cn(
                      "mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
                      "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
                    )}
                    value={createCarSource}
                    onChange={(event) => setCreateCarSource(event.target.value as CorrectiveActionSource)}
                  >
                    {CORRECTIVE_ACTION_SOURCES.map((source) => (
                      <option key={source} value={source}>
                        {correctiveActionSourceLabel(source)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="case-create-car-qc-incident-id">QC incident id (optional)</Label>
                  <Input
                    id="case-create-car-qc-incident-id"
                    value={createCarQualityIncidentId}
                    onChange={(event) => setCreateCarQualityIncidentId(event.target.value)}
                    placeholder="Link an existing QC incident"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="case-create-car-problem">Problem description</Label>
                <TextArea
                  id="case-create-car-problem"
                  value={createCarProblem}
                  onChange={(event) => setCreateCarProblem(event.target.value)}
                  rows={4}
                  placeholder="What happened, where discovered, and customer or production impact"
                />
              </div>
              <div>
                <Label htmlFor="case-create-car-containment">Immediate containment (optional)</Label>
                <TextArea
                  id="case-create-car-containment"
                  value={createCarContainment}
                  onChange={(event) => setCreateCarContainment(event.target.value)}
                  rows={3}
                  placeholder="Quarantine, stop-ship, notify customer, or other immediate controls"
                />
              </div>
              <p className="text-xs text-slate-500">
                Owner, sales order, and client copy from this case. You can change them on the CAR after it opens.
              </p>
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="secondary" disabled={createCarMutation.isPending} onClick={() => setShowCreateCar(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={createCarMutation.isPending || createCarTitle.trim() === ""}
                  onClick={() => createCarMutation.mutate()}
                >
                  {createCarMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create CAR
                </Button>
              </div>
            </div>
          ) : null}

          {writesAllowed ? (
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[12rem] flex-1">
                <Label htmlFor="link-car-id">CAR id</Label>
                <Input
                  id="link-car-id"
                  value={carIdToLink}
                  onChange={(event) => setCarIdToLink(event.target.value)}
                  placeholder="Corrective action report id"
                />
              </div>
              <Button
                disabled={linkCarMutation.isPending || carIdToLink.trim() === ""}
                onClick={() => {
                  const parsed = Number(carIdToLink);
                  if (!Number.isFinite(parsed) || parsed < 1) {
                    showToast({ tone: "error", message: "CAR id must be a positive integer." });
                    return;
                  }
                  linkCarMutation.mutate(parsed);
                }}
              >
                Link CAR
              </Button>
            </div>
          ) : null}

          {(detailQuery.data?.correctiveActions.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-600">No corrective action reports linked to this case.</p>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-2">CAR</th>
                    <th className="px-2 py-2">Title</th>
                    <th className="px-2 py-2">Status</th>
                    {writesAllowed ? <th className="px-2 py-2">Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {detailQuery.data?.correctiveActions.map((report) => (
                    <tr key={report.id} className="border-b border-slate-100">
                      <td className="px-2 py-2">
                        <Link
                          className="text-sky-700 hover:underline"
                          to={`/quality/corrective-actions/${report.id}`}
                        >
                          {report.carNumber}
                        </Link>
                      </td>
                      <td className="px-2 py-2">{report.title}</td>
                      <td className="px-2 py-2">
                        {CORRECTIVE_ACTION_STATUSES.includes(report.status as CorrectiveActionStatus)
                          ? correctiveActionStatusLabel(report.status as CorrectiveActionStatus)
                          : report.status}
                      </td>
                      {writesAllowed ? (
                        <td className="px-2 py-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={unlinkCarMutation.isPending}
                            onClick={() => unlinkCarMutation.mutate(report.id)}
                          >
                            Unlink
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <QualityCaseRecordGapsCard />
      <QualityCaseActivityCard caseId={qualityCase.id} />
    </div>
  );
}
